/**
 * product.gql-map.ts — the PURE mapping layer between the product graph and
 * the legacy REST types (CU-1b products third; masters/taxonomy/pricing
 * precedent).
 *
 * Import-free of the axios/i18n stack so the vitest matrix can exercise every
 * derivation in the plain node environment.
 *
 * ── THE TWO RULES THIS FILE EXISTS TO KEEP ────────────────────────────────
 *
 * 1. KEY PRESENCE IS PART OF THE CONTRACT (the CU-1a bank-wire lesson, and the
 *    one that costs a whole shadow run to relearn). `mocks/data/products/
 *    products.ts::seedProduct` is the SPEC for which keys a product object
 *    carries, and it is not uniform: `scn`, `netSuiteItemId`, `oemNo`, `mpn`,
 *    `upcCode`, `costingMethod` and `basePrice` are ALWAYS present and may be
 *    null, while `salesDescription`, `variant`, `isShared`, `damageStockNotes`,
 *    `minQty`, `accounting`, `planning` and the rest are simply ABSENT when
 *    unset (the seed assigns `undefined` and `JSON.stringify` drops the key).
 *    Measured over the 44-product corpus: `damageStockNotes` is present on 1,
 *    `planning` on 7, `isShared` on 13, `fitment` on 17, `accounting` on 41.
 *    Emitting `null` for those would byte-diff every row of the shadow leg —
 *    and, in the browser, would turn "never entered" into "explicitly empty".
 *    `ALWAYS_NULLABLE` below is that spec, transcribed, not inferred.
 *
 * 2. NOTHING DERIVED IS RE-DERIVED HERE. `enrichProduct` (@senditship/domain)
 *    is PURE and shared, and it is the SAME function the MSW handler calls on
 *    every read. `categoryPath`, `cubicFeet`, `name`, `baseUom`,
 *    `customerDescription`, `lengthCm/widthCm/heightCm`, `cbm`,
 *    `preferredVendorId/Name`, `listPrice`, `readiness`, `occStatus` and
 *    `occBlockedFields` therefore match by CONSTRUCTION rather than by a
 *    second implementation that has to be kept in step. This file's job is to
 *    rebuild the RAW product faithfully and then get out of the way.
 *
 * ── UNITS ────────────────────────────────────────────────────────────────
 * The graph is ADR-008 canonical: cm/kg decimal strings at pinned scales. The
 * legacy wire is gold-era mm plus a display unit. Conversions here are the
 * INVERSE of the seed boundary's (`persistence.seed.ts` mmToCm /
 * toCanonicalKg) and are exact at these scales — cm→mm shifts one place, and
 * kg→g three. A weight entered in grams comes back in grams because
 * `weightEntryUnit` records what was typed; the stored value stays kg.
 */

import { enrichProduct } from '@senditship/domain/rules/product';

import type {
  GqlProductWire,
  GqlProductVendorLine,
  GqlUserError,
} from './product.operations';
import type { ProductCategory, CategoryDepth } from '@/types/api/productCategory';
import type {
  Product,
  ProductAccountingRefs,
  ProductCostingMethod,
  ProductCustomerPartNumber,
  ProductFitment,
  ProductFitmentYearRange,
  ProductImageSlot,
  ProductNote,
  ProductReplenishmentPlanning,
  ProductVendorLine,
  ProductWeightUnit,
} from '@/types/api/product';

/**
 * The legacy REST refusal, rebuilt from a graph `userErrors` row.
 *
 * The product consumers read failures off `error.response.data` — the wizard's
 * dup-check FSM reads `error.response.status === 409` and
 * `data.conflict` (see `skuTakenResponse` in mocks/handlers/product.ts). The
 * live leg rebuilds that exact axios-shaped object so no consumer learns a
 * second error shape, which is what makes the flip invisible to the UI.
 */
export class LegacyProductError extends Error {
  readonly response: { status: number; data: Record<string, unknown> };

  constructor(status: number, data: Record<string, unknown>, message: string) {
    super(message);
    this.name = 'LegacyProductError';
    this.response = { status, data };
  }
}

/**
 * Guard code → legacy HTTP status, VERBATIM from the mock handler. Anything
 * absent is 400 — the handler's own default for a refused write.
 */
export const PRODUCT_GUARD_STATUS: Readonly<Record<string, number>> = {
  SKU_TAKEN: 409,
  NOT_FOUND: 404,
  STALE_VERSION: 409,
  VALIDATION_FAILED: 400,
};

/** The mock's 404, for a graph read that answered null. */
export function throwProductNotFound(): never {
  throw new LegacyProductError(404, { error: 'NOT_FOUND', message: 'Product not found' }, 'Product not found');
}

/**
 * The mock's refusal MESSAGE template, verbatim.
 *
 * Rebuilt rather than passed through: the graph's `userError` message for the
 * same guard ends in a full stop and the mock's does not, and the shadow leg
 * byte-diffs refusals as strictly as successes. The handler is the spec, so
 * this is a copy of it, not a re-derivation.
 */
export function productGuardMessage(code: string, internalProductId?: string | null): string {
  if (code === 'SKU_TAKEN') return `SKU already exists on ${internalProductId ?? ''}`;
  return code;
}

/**
 * Turn a non-empty `userErrors` list into the legacy refusal. The FIRST row
 * wins — the mock refuses on the first violated guard too.
 *
 * SKU_TAKEN is special. The mock answers 409 with `conflictId` — the RAW id of
 * the row that already holds the SKU — plus a message naming its
 * internalProductId, and the wizard's dup-check FSM reads both. The graph
 * reports the code and the SKU, so the caller resolves the conflicting row and
 * hands it in; a bare code would leave the wizard unable to say which product
 * is in the way.
 */
export function throwProductGuard(
  userErrors: readonly GqlUserError[],
  conflict?: { id: string; internalProductId: string } | null
): void {
  if (userErrors.length === 0) return;
  const first = userErrors[0];
  const status = PRODUCT_GUARD_STATUS[first.code] ?? 400;
  if (first.code === 'SKU_TAKEN') {
    const message = productGuardMessage('SKU_TAKEN', conflict?.internalProductId);
    throw new LegacyProductError(
      status,
      { error: 'SKU_TAKEN', message, conflictId: conflict?.id ?? null },
      message
    );
  }
  throw new LegacyProductError(
    status,
    { error: first.code, message: first.message },
    first.message
  );
}

/* -------------------------------------------------------------- helpers */

/** Decimal string → number, preserving "absent" as undefined (never 0). */
function num(value: string | null | undefined): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Canonical cm (decimal string) → legacy mm.
 *
 * Exact at the pinned scale: `NUMERIC(7,1)` cm is at most one decimal place,
 * so ×10 is an integer shift. Done on the STRING's digits rather than through
 * a float so 22.1 cm cannot arrive as 220.99999999999997 mm.
 */
export function cmToMm(value: string | null | undefined): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const negative = value.startsWith('-');
  const [int, frac = ''] = (negative ? value.slice(1) : value).split('.');
  const shifted = `${int}${frac.padEnd(1, '0')}`;
  const digits = Number(shifted.replace(/^0+(?=\d)/, ''));
  if (!Number.isFinite(digits)) return undefined;
  const rest = frac.length > 1 ? Number(`0.${frac.slice(1)}`) : 0;
  return (negative ? -1 : 1) * (digits + rest);
}

/**
 * Canonical kg (decimal string) → the value in the unit it was ENTERED in.
 *
 * The inverse of `toCanonicalKg` at the seed boundary. Only kg and g round
 * trip: the seeder REFUSES lb ("needs a ruling before it can be converted
 * losslessly"), so this refuses to invent one either — an lb-flagged row
 * returns its canonical kg number rather than a made-up pound figure, and the
 * unit it reports is the one that was stored.
 */
export function kgToEntryUnit(
  kg: string | null | undefined,
  unit: string | null | undefined
): number | undefined {
  const value = num(kg);
  if (value === undefined) return undefined;
  if (unit === 'g') {
    // ×1000 on a ≤3-dp decimal string is an exact integer shift.
    return Math.round(value * 1000);
  }
  return value;
}

/** Drop keys whose value is `undefined`, so JSON.stringify omits them. */
function compact<T extends Record<string, unknown>>(input: T): T {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    if (value !== undefined) out[key] = value;
  }
  return out as T;
}

/**
 * The graph's `taxonomyPath` (root→self, already resolved) as the minimal
 * `ProductCategory[]` `categoryChain` needs: it looks the target up by id and
 * then walks its materialized `path`. Rebuilding the path from the chain's
 * own ids reproduces the walk exactly, with no second taxonomy round trip and
 * no second implementation of the breadcrumb.
 */
export function chainCategories(
  path: readonly { id: string; labelEn: string; labelZh: string; depth: number }[]
): ProductCategory[] {
  const ids = path.map((node) => node.id);
  return path.map((node, index) => ({
    id: node.id,
    code: node.id,
    parentId: index === 0 ? null : ids[index - 1],
    depth: node.depth as CategoryDepth,
    path: ids.slice(0, index + 1).join('/'),
    sortOrder: index,
    nameEn: node.labelEn,
    nameZh: node.labelZh,
    isActive: true,
  }));
}

/* ------------------------------------------------------------ relations */

export function mapVendorLine(line: GqlProductVendorLine): ProductVendorLine {
  return compact({
    id: line.id,
    productId: line.productId,
    vendorId: line.vendorId,
    vendorName: line.vendorName ?? '',
    vendorSku: line.vendorSku ?? undefined,
    unitCost: num(line.unitCost) ?? 0,
    currency: line.currencyCode ?? '',
    isPrimary: line.isPrimary,
    // The deprecated mirror the mock keeps in lockstep with isPrimary.
    isPreferred: line.isPrimary,
    leadTimeDays: line.leadTimeDays ?? undefined,
    lastPurchasePrice: num(line.lastPurchasePrice),
    nextPurchasePrice: num(line.nextPurchasePrice),
    moq: line.moq ?? undefined,
    isActive: line.isActive,
  }) as ProductVendorLine;
}

function mapCustomerPartNumber(
  cpn: GqlProductWire['customerPartNumbers'][number]
): ProductCustomerPartNumber {
  return compact({
    id: cpn.id,
    customerId: cpn.customerId,
    customerName: cpn.customerName ?? '',
    partNumber: cpn.partNumber,
    description: cpn.description ?? undefined,
    isAuthorized: cpn.isAuthorized ?? undefined,
    // The corpus carries these as EXPLICIT nulls (the dormant Customer Item
    // Authorization window), so they stay null rather than being dropped.
    effectiveFrom: cpn.effectiveFrom,
    effectiveTo: cpn.effectiveTo,
  }) as ProductCustomerPartNumber;
}

function mapNote(note: GqlProductWire['notes'][number]): ProductNote {
  return {
    id: note.id,
    // SDL calls it `body`; the legacy wire calls it `text`.
    text: note.body,
    audience: note.audience.toLowerCase() as ProductNote['audience'],
    createdAt: note.createdAt,
  };
}

function mapImage(image: GqlProductWire['images'][number]): ProductImageSlot {
  return { slot: image.slot as ProductImageSlot['slot'], url: image.url };
}

/**
 * The graph's fitment ROWS → the legacy multi-select object.
 *
 * The seeder writes one row per make×model pair (`fitmentRowsOf`), so the
 * inverse is the distinct makes and the distinct models — which reconstructs
 * the original set exactly for every shape the corpus contains. The year
 * ranges are identical on every row of one product (the seeder attaches the
 * whole `years` list to each), so the FIRST row's ranges are the product's.
 */
export function mapFitment(rows: readonly GqlProductWire['fitment'][number][]): ProductFitment | undefined {
  if (rows.length === 0) return undefined;
  const makes = [...new Set(rows.map((row) => row.make))];
  const models = [...new Set(rows.map((row) => row.model).filter((m): m is string => Boolean(m)))];
  const years: ProductFitmentYearRange[] = rows[0].yearRanges.map((range) =>
    compact({
      yearFrom: range.fromYear ?? 0,
      yearTo: range.toYear ?? undefined,
      variant: range.variant ?? undefined,
    }) as ProductFitmentYearRange
  );
  return { makes, models, years };
}

/**
 * The ×16 + tax schedule, mapped from the SDL's `*Ref` names onto the legacy
 * wire's `*AccountId` names. The naming divergence is recorded in the graph
 * module and in FREEZE.md amendment 3 — this table is the whole cost of it.
 *
 * Returns `undefined` when the graph has no refs row at all, because the mock
 * omits the `accounting` key entirely for the three corpus products that
 * carry none.
 */
export function mapAccountingRefs(
  refs: GqlProductWire['accountingRefs']
): ProductAccountingRefs | undefined {
  if (!refs) return undefined;
  return {
    incomeAccountId: refs.incomeAccountRef,
    cogsAccountId: refs.cogsAccountRef,
    assetAccountId: refs.assetAccountRef,
    deferredRevenueAccountId: refs.deferredRevenueAccountRef,
    dropshipExpenseAccountId: refs.dropshipExpenseAccountRef,
    gainLossAccountId: refs.gainLossAccountRef,
    priceVarianceAccountId: refs.priceVarianceAccountRef,
    quantityVarianceAccountId: refs.quantityVarianceAccountRef,
    exchangeRateVarianceAccountId: refs.exchangeRateVarianceAccountRef,
    vendorBillPriceVarianceAccountId: refs.vendorBillPriceVarianceAccountRef,
    vendorBillQuantityVarianceAccountId: refs.vendorBillQuantityVarianceAccountRef,
    vendorBillExchangeRateVarianceAccountId: refs.vendorBillExchangeRateVarianceAccountRef,
    vendorReturnVarianceAccountId: refs.vendorReturnVarianceAccountRef,
    customerReturnVarianceAccountId: refs.customerReturnVarianceAccountRef,
    intercompanyIncomeAccountId: refs.intercompanyIncomeAccountRef,
    intercompanyCogsAccountId: refs.intercompanyCogsAccountRef,
    taxScheduleId: refs.taxScheduleRef,
  };
}

/**
 * The nine stored planning members. The four DORMANT `*Auto` booleans and
 * `preferredStockLevelDays` are absent by design — no column, no corpus value,
 * no surface (B2.4b'-read named hole). Emitting them as null would claim the
 * planner had switched auto-calculation OFF, which nobody did.
 */
export function mapPlanning(
  plan: GqlProductWire['replenishmentPlanning']
): ProductReplenishmentPlanning | undefined {
  if (!plan) return undefined;
  return {
    reorderPoint: plan.reorderPointQty,
    preferredStockLevel: plan.preferredStockLevelQty,
    reorderMultiple: plan.reorderMultipleQty,
    purchaseLeadTimeDays: plan.leadTimeDays,
    safetyStockDays: plan.safetyStockDays,
    expectedDemandChangePct: num(plan.expectedDemandChangePct) ?? null,
    seasonalDemand: plan.seasonalDemand,
    preOrder: plan.preOrder,
    doNotOrder: plan.doNotOrder,
  };
}

/* --------------------------------------------------------- the product */

/**
 * A graph product → the RAW legacy product (pre-enrich).
 *
 * Read this beside `seedProduct` in mocks/data/products/products.ts: the key
 * ORDER differs (irrelevant — the diff sorts keys) but the key SET must not.
 * Fields written as `x ?? null` are the always-present nullable set; fields
 * written as `x ?? undefined` are dropped when unset.
 */
export function mapProductWireRaw(wire: GqlProductWire): Product {
  const upc = wire.packagingLevels.find((level) => level.levelCode === 'BASE_UNIT')?.gtin ?? null;
  return compact({
    // IDENTITY — the RAW id, never the opaque relay one (CU-1a detail-404).
    id: wire.productId,
    internalProductId: wire.internalProductId,
    scn: wire.scn ?? null,
    sku: wire.sku,
    displayName: wire.nameEn,

    // Classification
    subsidiaryId: wire.subsidiaryId ?? '',
    includeChildren: wire.includeChildren,
    categoryId: wire.categoryId,
    secondaryCategoryIds: wire.secondaryCategoryIds,

    // Units
    unitsTypeId: wire.unitsTypeId,
    stockUnit: wire.uom,
    purchaseUnit: wire.purchaseUom,
    saleUnit: wire.salesUom,

    // Descriptions
    salesDescription: wire.customerDescription ?? undefined,
    purchaseDescription: wire.vendorDescription ?? undefined,
    customsDescription: wire.customsDescription ?? undefined,
    purchaseOrderNote: wire.purchaseOrderNote ?? undefined,

    // External identifiers. `upcCode` is the BASE_UNIT packaging level's GTIN
    // (ADR-008 puts the barcode on the level, never on the product row) — two
    // corpus products carry a real one, so a hard null here would be false.
    netSuiteItemId: wire.netSuiteItemId ?? null,
    netSuiteSyncedAt: wire.netSuiteSyncedAt ?? null,
    oemNo: wire.oemNumber ?? null,
    mpn: wire.mpn ?? null,
    upcCode: upc,

    // Relations
    customerPartNumbers: wire.customerPartNumbers.map(mapCustomerPartNumber),
    images: wire.images.map(mapImage),
    fitment: mapFitment(wire.fitment),

    // Manufacturing / fitment attributes
    variant: wire.variant ?? undefined,
    extractYear: wire.extractYear ?? undefined,
    manufacturer: wire.manufacturer ?? undefined,
    countryOfManufacture: wire.countryOfManufacture ?? undefined,
    faSubCategory: wire.faSubCategory ?? undefined,
    ageGroup: wire.ageGroup ?? undefined,
    ageStartYear: wire.ageStartYear ?? undefined,
    ageEndYear: wire.ageEndYear ?? undefined,
    isShared: wire.isShared ?? undefined,

    // Notes
    productNotes: wire.notes.map(mapNote),
    damageStockNotes: wire.damageStockNotes ?? undefined,
    itemComment: wire.itemComment ?? undefined,

    // Physical — ADR-008 canonical cm/kg back to the legacy mm + entry unit.
    lengthMm: cmToMm(wire.lengthCm),
    widthMm: cmToMm(wire.widthCm),
    heightMm: cmToMm(wire.heightCm),
    weightKg: kgToEntryUnit(wire.weight, wire.weightEntryUnit),
    // Always present: `seedProduct` and the create handler both default 'kg'.
    weightUnit: (wire.weightEntryUnit ?? 'kg') as ProductWeightUnit,

    // Commercial
    costingMethod: (wire.costingMethod as ProductCostingMethod | null) ?? null,
    basePrice: num(wire.basePrice) ?? null,
    // Always present: both mock paths default 'AUD'.
    currency: wire.currencyCode ?? 'AUD',
    minQty: wire.minQty ?? undefined,
    minQtyUnit: wire.minQtyUnit ?? undefined,
    maxQty: wire.maxQty ?? undefined,
    maxQtyUnit: wire.maxQtyUnit ?? undefined,
    itemDefinedCost: num(wire.itemDefinedCost),
    itemDefinedCostUnit: wire.itemDefinedCostUnit ?? undefined,

    accounting: mapAccountingRefs(wire.accountingRefs),
    planning: mapPlanning(wire.replenishmentPlanning),

    isActive: wire.isActive,
    createdAt: wire.createdAt,
    updatedAt: wire.updatedAt,

    // Legacy mirrors enrichProduct recomputes; `nameZh` is stored, not derived.
    nameZh: wire.nameZh ?? undefined,
  }) as unknown as Product;
}

/**
 * A graph product → the ENRICHED legacy product the wire actually serves.
 *
 * The single call to `enrichProduct` is the point: every derived mirror comes
 * from the same function the mock handler runs, over the same raw shape, so
 * the two worlds cannot disagree about a derivation. Only the RAW mapping
 * above can drift, and that is what the shadow leg byte-diffs.
 */
export function mapProductWire(wire: GqlProductWire): Product {
  const raw = mapProductWireRaw(wire);
  const lines = wire.vendorLines.map(mapVendorLine);
  return enrichProduct(raw, chainCategories(wire.taxonomyPath), lines);
}

/** The `check-sku` conflict projection, mapped onto `CheckSkuResponse`. */
export function mapSkuConflict(conflict: {
  productId: string;
  internalProductId: string;
  sku: string;
  nameEn: string;
  isActive: boolean;
}): { id: string; internalProductId: string; sku: string; displayName: string; isActive: boolean } {
  return {
    id: conflict.productId,
    internalProductId: conflict.internalProductId,
    sku: conflict.sku,
    displayName: conflict.nameEn,
    isActive: conflict.isActive,
  };
}
